import { useEffect, useMemo, useState } from 'react';
import {
    Card,
    Button,
    Table,
    Typography,
    Space,
    Row,
    Col,
    Pagination,
    message,
    Popconfirm,
    Empty,
    Tag,
    Spin,
} from 'antd';
import moment from 'moment-timezone';
import { useAuth } from '../../../context/auth.context';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { SettingWrapper } from '../pages/style';
import { Main } from '../../styled';
import useApiFetch from '../../../hooks/useApiFetch';
import UpdateSubscriptionModel from '../../../components/UpdateSubscriptionModel/UpdateSubscrptionModel';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import ShowingAddon from '../../../components/ShowingAddons/ShowingAddon';

const { Title, Text } = Typography;

const MySubscription = () => {
    const { subscriptionPlan, apiClient } = useAuth();
    const subscription = subscriptionPlan || {};
    const isFreeTrial = subscription?.isFreeTrial;
    const hasPlan = !!subscription?.planId;
    const plan = subscription?.plan || {};
    const features = subscription?.features || [];
    const addons = subscription?.addons || [];
    const userCurrency = subscriptionPlan?.currency || 'USD';
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [modalType, setModalType] = useState(null); // Track modal type
    const [selectedFeature, setSelectedFeature] = useState(null); // Track selected feature
    const [currencyType, setCurrencyType] = useState('$');

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    //Fetch /me api to check subscription status and loading state
    const { data: meData, loading: meLoading } = useApiFetch('/api/org-users/me');
    const subscriptionStatus = meData?.data[0]?.organization?.subscriptionStatus || '';
    const isFreeTrailUsed = meData?.data[0]?.organization?.freeTrialUsed || false;

    const apiUrl = hasPlan
        ? `/api/subscriptions/transactions?take=${pagination.pageSize}&skip=${(pagination.current - 1) * pagination.pageSize
        }`
        : null;
    const { data: transactionRes, refetch } = useApiFetch(apiUrl);

    useEffect(() => {
        if (transactionRes?.meta?.total !== undefined) {
            setPagination((prev) => ({
                ...prev,
                total: transactionRes.meta.total,
            }));
        }
    }, [transactionRes]);

    useEffect(() => {
        if (subscriptionPlan?.currency === 'INR') {
            setCurrencyType('₹');
        } else if (subscriptionPlan?.currency === 'USD') {
            setCurrencyType('$');
        } else if (subscriptionPlan?.currency === 'EUR') {
            setCurrencyType('€');
        } else if (subscriptionPlan?.currency === 'SGD') {
            setCurrencyType('S$');
        }
    }, [subscriptionPlan]);

    // Fetch all addons for the current plan
    const {
        data: fetchedAddons = [],
        loading: addonsLoading,
        error: addonsError,
    } = useApiFetch(hasPlan ? `/api/subscriptions/addons?planId=${subscription?.planId}` : null, (res) => res.data);

    const startDate = subscription?.startDate && moment(subscription?.startDate).format('DD MMM, YYYY');
    const expiryDate = subscription?.expiryDate && moment(subscription?.expiryDate).format('DD MMM, YYYY');
    const billingAmount = subscription?.totalAmount && `${currencyType}${subscription.totalAmount}`;

    // Cancel Subscription Plan Function
    const cancelSubscriptionPlan = async () => {
        try {
            await apiClient.post(`/api/subscriptions/cancel-plan`);
            message.success('Subscription plan cancelled successfully');
            window.location.reload();
        } catch (error) {
            message.error('Failed to delete subscription plan');
        }
    };

    // Handle Add Addon - Updated to work with ShowingAddon modal
    const handleAddAddon = async (featureRecord) => {
        setSelectedFeature(featureRecord); // contains availableAddons with prices
        setModalType('addons');
    };

    // Handle Upgrade Plan from ShowingAddon modal
    const handleUpgradePlan = () => {
        setModalType('pricing');
    };

    // Handle modal close/back
    const handleModalClose = () => {
        setModalType(null);
        setSelectedFeature(null);
    };
    const getPriceForUser = (plan, strict = false) => {
        const preferredCurrency = subscriptionPlan?.currency;
        const priceData = plan.prices.find((p) => p.currency === preferredCurrency);

        if (strict) {
            return priceData || null; // Return only if preferred currency exists
        }

        if (priceData) return priceData;

        const fallbackPrice = plan.prices.find((p) => p.currency === 'USD');
        if (fallbackPrice) return fallbackPrice;

        return plan.prices.length > 0 ? plan.prices[0] : null;
    };

    // Usage Table Data (LIMIT features)
    const usageData = useMemo(() => {
        return features
            .filter((f) => f.type === 'limit' && f.isEnabled)
            .map((f, index) => ({
                key: index,
                featureName: f.feature?.name || '-',
                used: f.usedLimit || '0',
                total: f.totalLimit || 'n/a',
                action: 'Add More',
                feature: f, // Store the full feature object
            }));
    }, [features]);

    const usageColumns = useMemo(() => {
        const baseColumns = [
            { title: 'Feature name', dataIndex: 'featureName', key: 'featureName' },
            { title: 'Used', dataIndex: 'used', key: 'used' },
            { title: 'Total', dataIndex: 'total', key: 'total' },
        ];

        const actionColumn = {
            title: 'Actions',
            dataIndex: 'action',
            key: 'action',
            render: (_, record) => {
                const addons = record.feature?.feature?.addons || [];
                // Filter addons that have price in preferred currency
                const addonsWithPreferredPrice = addons.filter((addon) => getPriceForUser(addon, true) !== null);

                const hasValidAddons = addonsWithPreferredPrice.length > 0;

                return (
                    <Button
                        type="default"
                        style={hasValidAddons ? { borderColor: 'blue', color: 'blue', cursor: 'pointer' } : {}}
                        size="small"
                        onClick={() => handleAddAddon(record)}
                        disabled={!hasValidAddons}
                    >
                        Increase Limit
                    </Button>
                );
            },
            fixed: 'right',
        };

        return isFreeTrial ? baseColumns : [...baseColumns, actionColumn];
    }, [features, isFreeTrial]);

    // Disabled Features Table (BOOLEAN features)
    const disabledFeaturesData = useMemo(() => {
        return features
            .filter((f) => f.type === 'boolean' && f.isEnabled)
            .map((f, index) => ({
                key: index,
                featureName: f.feature?.name || '-',
                isEnabled: f.isEnabled,
                feature: f,
            }));
    }, [features]);

    const disabledFeaturesColumns = [
        { title: 'Feature name', dataIndex: 'featureName', key: 'featureName' },
        {
            title: 'Enabled',
            dataIndex: 'isEnabled',
            key: 'isEnabled',
            render: (value) => (value ? 'Yes' : 'No'),
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => {
                const featureId = record.feature?.featureId || record.feature?.id;

                // Get addons for this feature from API response
                const featureAddons = fetchedAddons?.filter((addon) => addon.featureId === featureId);

                // Check if **any addon** has price in preferred currency
                const preferredCurrency = subscriptionPlan?.currency;

                const hasValidPricing = featureAddons?.some((addon) =>
                    addon.prices?.some((p) => p.currency === preferredCurrency),
                );
                const hasAddons = featureAddons?.length > 0;

                return (
                    <Button
                        type="default"
                        size="small"
                        style={
                            record.isEnabled || !hasAddons
                                ? {}
                                : { borderColor: 'blue', color: 'blue', cursor: 'pointer' }
                        }
                        onClick={() => {
                            // Pass prices along with feature to the modal
                            handleAddAddon({
                                ...record,
                                availableAddons: featureAddons,
                            });
                        }}
                        disabled={!hasAddons || !hasValidPricing}
                    >
                        {!hasAddons ? 'Enabled' : 'Enable'}
                    </Button>
                );
            },
        },
    ];

    // Addons Table
    const addonsColumns = [
        {
            title: 'Addon Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            render: (price) => (price != null && price !== '' ? `${currencyType}${price}` : ' - '),
        },
        {
            title: 'Recurring',
            dataIndex: 'isRecurring',
            key: 'isRecurring',
            render: (recurring) =>
                recurring ? (
                    <Tag color="green">
                        <CheckOutlined /> Yes
                    </Tag>
                ) : (
                    <Tag color="red">
                        <CloseOutlined /> No
                    </Tag>
                ),
        },
        {
            title: 'Unit Limit',
            dataIndex: 'unitLimit',
            key: 'unitLimit',
            render: (limit) => (limit !== null ? limit : 'Unlimited'),
        },
    ];

    const formattedAddons = useMemo(() => {
        return fetchedAddons?.map((addon) => {
            const firstPrice = addon.prices?.[0]; // Display first price for now
            return {
                key: addon.id,
                name: addon.feature?.name || 'Unknown Feature',
                unitPrice: firstPrice && firstPrice.unitPrice ? `${firstPrice.unitPrice}` : null,
                isRecurring: addon.isRecurring,
                unitLimit: addon.unitLimit !== null ? addon.unitLimit : 'Unlimited',
            };
        });
    }, [fetchedAddons, currencyType]);

    // Transactions Table
    const transactionData = useMemo(
        () =>
            (transactionRes?.data || []).map((t) => ({
                key: t.id,
                id: t.id,
                date: t.createdAt ? moment(t.createdAt).format('DD MMM YYYY, hh:mm a') : '-',
                amount: t.amount !== undefined ? `${currencyType}${t.amount}` : '-',
                currency: t.currency || '-',
                status: t.status || '-',
                razorpayOrderId: t.razorpayOrderId || 'N/A',
                razorpayPaymentId: t.razorpayPaymentId || 'N/A',
                razorpaySubscriptionId: t.razorepaySubscriptionId || 'N/A',
            })),
        [transactionRes],
    );

    const transactionColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Amount', dataIndex: 'amount', key: 'amount' },
        { title: 'Currency', dataIndex: 'currency', key: 'currency' },
        { title: 'Status', dataIndex: 'status', key: 'status' },
        { title: 'Subscription ID', dataIndex: 'razorpaySubscriptionId', key: 'razorpaySubscriptionId' },
        { title: 'Payment ID', dataIndex: 'razorpayPaymentId', key: 'razorpayPaymentId' },
    ];

    //loading state for subscription activation
    if (meLoading || subscriptionStatus === 'activating') {
        return (
            <Main>
                <div style={{ textAlign: 'center', paddingTop: 100 }}>
                    <Spin size="large" />
                    <Title level={4} style={{ marginTop: 20 }}>
                        {meLoading ? 'Loading your subscription...' : 'Activating your subscription...'}
                    </Title>
                </div>
            </Main>
        );
    }
    return (
        <>
            <PageHeader
                className="subscription-page-header"
                ghost
                title="My Subscription"
                routes={[
                    { path: '/admin/overview', icon: 'home' },
                    { path: '/admin/settings', breadcrumbName: 'Settings' },
                    { path: '/admin/settings/my-subscription', breadcrumbName: 'My Subscription' },
                ]}
            />

            <Main className="subscription-main">
                <SettingWrapper className="subscription-page">
                    <div className="subscription-content">
                        {subscriptionPlan ? (
                            <>
                                <Card
                                    title={<Title level={5}>Plan Details</Title>}
                                    extra={
                                        <div className="subscription-actions">
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    setIsModalOpen(true);
                                                }}
                                            >
                                                Update Plan
                                            </Button>
                                            {subscriptionPlan && (
                                                <Popconfirm
                                                    title="Are you sure you want to cancel this subscription plan?"
                                                    onConfirm={cancelSubscriptionPlan}
                                                    okText="Yes"
                                                    cancelText="No"
                                                    disabled={!hasPlan}
                                                >
                                                    <Button type="text" danger disabled={!hasPlan}>
                                                        Cancel Plan
                                                    </Button>
                                                </Popconfirm>
                                            )}
                                        </div>
                                    }
                                >
                                    <Row style={{ marginBottom: 8 }}>
                                        <Col xs={24} sm={8}>
                                            <Text strong>Plan Name:</Text>
                                        </Col>
                                        <Col xs={24} sm={16}>
                                            <Text>{plan?.name || 'Unsubscribed'}</Text>
                                        </Col>
                                    </Row>
                                    {plan.billingCycle && (
                                        <Row style={{ marginBottom: 8 }}>
                                            <Col xs={24} sm={8}>
                                                <Text strong>Billing Cycle:</Text>
                                            </Col>
                                            <Col xs={24} sm={16}>
                                                <Text>{plan.billingCycle}</Text>
                                            </Col>
                                        </Row>
                                    )}

                                    {startDate && (
                                        <Row style={{ marginBottom: 8 }}>
                                            <Col xs={24} sm={8}>
                                                <Text strong>Start Date:</Text>
                                            </Col>
                                            <Col xs={24} sm={16}>
                                                <Text>{startDate}</Text>
                                            </Col>
                                        </Row>
                                    )}

                                    {expiryDate && (
                                        <Row style={{ marginBottom: 8 }}>
                                            <Col xs={24} sm={8}>
                                                <Text strong>Next Billing Date:</Text>
                                            </Col>
                                            <Col xs={24} sm={16}>
                                                <Text>{expiryDate}</Text>
                                            </Col>
                                        </Row>
                                    )}

                                    {billingAmount && (
                                        <Row>
                                            <Col xs={24} sm={8}>
                                                <Text strong>Next Billing Amount:</Text>
                                            </Col>
                                            <Col xs={24} sm={16}>
                                                <Text>{billingAmount}</Text>
                                            </Col>
                                        </Row>
                                    )}
                                </Card>

                                {!isFreeTrial && (
                                    <Card title={<Title level={5}>Purchased Addons</Title>}>
                                        {addonsLoading ? (
                                            <Text>Loading addons...</Text>
                                        ) : fetchedAddons?.length > 0 ? (
                                            <Table
                                                dataSource={formattedAddons}
                                                columns={addonsColumns}
                                                rowKey="key"
                                                pagination={false}
                                                bordered
                                            />
                                        ) : (
                                            <Empty description="No addons purchased" />
                                        )}
                                    </Card>
                                )}

                                {subscriptionPlan && (
                                    <Card title={<Title level={5}>Usage</Title>}>
                                        <Table
                                            dataSource={usageData}
                                            columns={usageColumns}
                                            pagination={false}
                                            bordered
                                        />
                                    </Card>
                                )}

                                {!isFreeTrial && subscriptionPlan && (
                                    <Card title={<Title level={5}>Additional Features</Title>}>
                                        <Table
                                            dataSource={disabledFeaturesData}
                                            columns={disabledFeaturesColumns}
                                            pagination={false}
                                            bordered
                                        />
                                    </Card>
                                )}

                                {subscriptionPlan && (
                                    <Card title={<Title level={5}>Transactions</Title>}>
                                        <Table
                                            dataSource={transactionData}
                                            columns={transactionColumns}
                                            pagination={false}
                                            bordered
                                        />
                                        <Space
                                            style={{ justifyContent: 'space-between', width: '100%', marginTop: 16 }}
                                            align="center"
                                        >
                                            <Text>
                                                {pagination.total > 0
                                                    ? `${(pagination.current - 1) * pagination.pageSize + 1
                                                    } - ${Math.min(
                                                        pagination.current * pagination.pageSize,
                                                        pagination.total,
                                                    )} of ${pagination.total} transactions`
                                                    : '0 transactions'}
                                            </Text>
                                            <Pagination
                                                current={pagination.current}
                                                pageSize={pagination.pageSize}
                                                total={pagination.total}
                                                showSizeChanger
                                                pageSizeOptions={['5', '10', '20']}
                                                onChange={(page, pageSize) => {
                                                    const skip = (page - 1) * pageSize;
                                                    const newUrl = `/api/subscriptions/transactions?take=${pageSize}&skip=${skip}`;
                                                    refetch(newUrl);
                                                    setPagination({
                                                        current: page,
                                                        pageSize,
                                                        total: pagination.total,
                                                    });
                                                }}
                                                showLessItems
                                            />
                                        </Space>
                                    </Card>
                                )}

                                {modalType === 'addons' && selectedFeature && (
                                    <ShowingAddon
                                        open={modalType === 'addons'}
                                        onBack={handleModalClose}
                                        featureKey={selectedFeature?.feature?.key}
                                        onUpgradePlan={handleUpgradePlan}
                                        subscriptionData={subscriptionPlan}
                                        customAddons={selectedFeature?.availableAddons || []}
                                        userCurrency={userCurrency}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Card>
                                    <div className="subscription-empty-state">
                                        <Title level={5}>No Active Subscription Plan Found</Title>
                                        <div className="subscription-actions">
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    setIsModalOpen(true);
                                                }}
                                                disabled={isFreeTrailUsed}
                                            >
                                                Start Free Trail
                                            </Button>{' '}
                                            <Button
                                                type="default"
                                                onClick={() => {
                                                    setIsModalOpen(true);
                                                }}
                                            >
                                                Subscribe Now
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                    <UpdateSubscriptionModel
                        visible={isModalOpen}
                        mode="update"
                        onClose={() => setIsModalOpen(false)}
                    />
                </SettingWrapper>
            </Main>
        </>
    );
};

export default MySubscription;
